import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TemperatureBadge, StatusBadge, UserAvatar } from "@/components/crm/Badges";
import {
  fullName, fmtDateTime, relDate, isOverdue, fmtEuro, TEMPERATURES, TEMP_ORDER,
  FOLLOWUP_OPTIONS, APPT_MODES, APPT_TYPES,
  isInvalidPhone,
} from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Phone, StickyNote, CalendarPlus, Flame,
  ArrowLeft, Loader2, Mail, MapPin, Building2, Megaphone, UserCog,
  PhoneCall, PhoneMissed, AlertTriangle, Euro, Trophy, XCircle, Unlock, History, Pencil, Trash2, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { EditAppointmentModal } from "@/components/crm/EditAppointmentModal";
import { EditActivityModal } from "@/components/crm/EditActivityModal";
import { ActivityActions } from "@/components/crm/NextActionCell";
import WhatsAppLeadDialog from "@/components/crm/WhatsAppLeadDialog";
import EditLeadModal from "@/components/crm/EditLeadModal";
import { useApptAvailability, ApptConflictBanner } from "@/components/crm/useApptAvailability";

const REQUIRED_ACTION_STATUSES = ["Mandare mail"];

const EVENT_META = {
  acquisition: { icon: "📥", label: "Lead acquisito" },
  call: { icon: "📞", label: "Chiamata" },
  contact_attempt: { icon: "📵", label: "Tentativo di contatto" },
  note: { icon: "📝", label: "Nota" },
  note_edited: { icon: "✏️", label: "Nota modificata" },
  lead_edited: { icon: "✏️", label: "Scheda modificata" },
  whatsapp: { icon: "💬", label: "Follow-up WhatsApp" },
  status_change: { icon: "🔄", label: "Cambio stato" },
  temperature_change: { icon: "🌡️", label: "Cambio temperatura" },
  followup_created: { icon: "⏰", label: "Follow-up programmato" },
  appointment_created: { icon: "📅", label: "Appuntamento fissato" },
  appointment_status: { icon: "✅", label: "Esito appuntamento" },
  appointment_rescheduled: { icon: "🔁", label: "Appuntamento riprogrammato" },
  appointment_edited: { icon: "✏️", label: "Appuntamento modificato" },
  appointment_deleted: { icon: "🗑️", label: "Appuntamento eliminato" },
  activity_edited: { icon: "✏️", label: "Attività modificata" },
  activity_deleted: { icon: "🗑️", label: "Attività eliminata" },
  activity_completed: { icon: "✅", label: "Attività completata" },
  reassignment: { icon: "👤", label: "Riassegnazione" },
  assignment: { icon: "👤", label: "Assegnazione" },
  deal_value_change: { icon: "💶", label: "Valore trattativa" },
  deal_won: { icon: "🏆", label: "Trattativa vinta" },
  deal_lost: { icon: "❌", label: "Trattativa persa" },
  deal_reopened: { icon: "🔓", label: "Trattativa riaperta" },
  deal_outcome_changed: { icon: "🔁", label: "Esito trattativa modificato" },
  won: { icon: "🎉", label: "Cliente acquisito" },
  merge: { icon: "🔗", label: "Lead unito" },
};

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [lead, setLead] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);
  const [editAppt, setEditAppt] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [showEditLead, setShowEditLead] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
    } catch { toast.error("Lead non trovato"); navigate("/leads"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/statuses").then((r) => setStatuses(r.data));
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "commerciale")));
  }, [isAdmin]);

  if (!lead) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const quickTemp = async (t) => {
    await api.post(`/leads/${id}/temperature`, { temperature: t });
    toast.success("Temperatura aggiornata");
    load();
  };
  const changeStatus = async (s) => {
    if (REQUIRED_ACTION_STATUSES.includes(s)) { setPendingStatus(s); return; }
    await api.post(`/leads/${id}/status`, { status: s });
    toast.success("Stato aggiornato");
    await load();
    if (s === "Follow-up") setModal("followup");
  };
  const updateField = async (field, value) => {
    await api.put(`/leads/${id}`, { [field]: value });
    toast.success("Aggiornato");
    load();
  };
  const reassign = async (uid) => {
    await api.post(`/leads/${id}/assign`, { assigned_to: uid });
    toast.success("Lead riassegnato");
    load();
  };
  const markAttempt = async () => {
    try {
      await api.post(`/leads/${id}/attempt`);
      toast.success("Tentativo registrato");
      load();
    } catch {
      toast.error("Impossibile registrare il tentativo");
    }
  };
  const canWork = isAdmin || (user && user.id === lead.assigned_to);
  // Attività collegata a "Prossimo contatto": task con la stessa data, altrimenti il primo appuntamento DA FARE
  const pendingTasks = (lead.tasks || []).filter((t) => t.status === "pending").sort((a, b) => (a.due_at || "").localeCompare(b.due_at || ""));
  const nextAppt = (lead.appointments || []).filter((a) => a.status === "DA FARE").sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""))[0];
  const ref = lead.next_ref;
  const refTask = ref?.kind === "task" ? pendingTasks.find((t) => t.id === ref.id) : null;
  const refAppt = ref?.kind === "appointment" ? (lead.appointments || []).find((a) => a.id === ref.id && a.status === "DA FARE") : null;
  const matchTask = refTask || pendingTasks.find((t) => t.due_at === lead.next_contact_at);
  const nextActivity = refAppt ? { kind: "appt", item: refAppt }
    : matchTask ? { kind: "task", item: matchTask }
    : nextAppt ? { kind: "appt", item: nextAppt }
    : pendingTasks[0] ? { kind: "task", item: pendingTasks[0] } : null;

  const ACTIONS = [
    { key: "call", label: "Chiama", icon: Phone, cls: "bg-emerald-500 hover:bg-emerald-600 text-white" },
    { key: "note", label: "Note", icon: StickyNote },
    { key: "appointment", label: "Appuntamento", icon: CalendarPlus },
    { key: "temperature", label: "Temperatura", icon: Flame },
  ];

  return (
    <div className="space-y-4 animate-fade-up pb-24 md:pb-0">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </button>

      {/* Header */}
      <div className="rounded-lg border bg-card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">{fullName(lead)}</h1>
              <TemperatureBadge value={lead.temperature} size="md" />
              {lead.no_next_action && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 bg-amber-100 dark:bg-amber-950 px-2 py-1 rounded">
                  <AlertTriangle className="w-3.5 h-3.5" /> Nessuna prossima azione
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
              {lead.company && <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" />{lead.company}</span>}
              {lead.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" />{lead.email}</span>}
              {(lead.city || lead.province) && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[lead.city, lead.province].filter(Boolean).join(", ")}</span>}
            </div>
          </div>
          {isInvalidPhone(lead.phone) ? (
            <div data-testid="invalid-phone-alert" className="rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-300 max-w-xs">
              <div className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" />Telefono non valido</div>
              <div className="text-xs mt-0.5">Valore attuale: <code className="font-mono">{lead.phone}</code></div>
              {canWork && <button data-testid="invalid-phone-fix-btn" onClick={() => setShowEditLead(true)} className="mt-1.5 text-xs font-semibold underline">Correggi numero</button>}
            </div>
          ) : lead.phone && (
            <a href={`tel:${lead.phone}`} data-testid="lead-phone-link"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-emerald-500 text-white font-semibold hover:bg-emerald-600 transition-colors">
              <PhoneCall className="w-4 h-4" /> {lead.phone}
            </a>
          )}
          <div className="flex flex-col gap-2">
            <button data-testid="lead-whatsapp-btn" onClick={() => setWaOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium text-sm hover:bg-emerald-500/20 transition-colors">
              <MessageCircle className="w-4 h-4" /> Apri WhatsApp
            </button>
            {canWork && (
              <button data-testid="lead-edit-btn" onClick={() => setShowEditLead(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md border font-medium text-sm hover:bg-accent transition-colors">
                <Pencil className="w-4 h-4" /> Modifica
              </button>
            )}
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <MetaCell label="Stato">
            <Select value={lead.status} onValueChange={changeStatus}>
              <SelectTrigger data-testid="status-select" className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.filter((s) => s.active !== false || s.name === lead.status).map((s) => (
                <SelectItem key={s.id} value={s.name} disabled={s.active === false} data-testid={`status-opt-${s.name}`}>
                  {s.name}{s.active === false ? " (non più in uso)" : ""}
                </SelectItem>
              ))}</SelectContent>
            </Select>
          </MetaCell>
          <MetaCell label="Interesse"><span className="text-sm">{(lead.interests || []).join(", ") || "—"}</span></MetaCell>
          <MetaCell label="Fonte / Sorgente"><span className="text-sm">{lead.fonte}<br /><span className="text-xs text-muted-foreground">{lead.sorgente}</span></span></MetaCell>
          <MetaCell label="Campagna"><span className="text-sm inline-flex items-center gap-1"><Megaphone className="w-3.5 h-3.5 text-muted-foreground" />{lead.campaign || "—"}</span></MetaCell>
          <MetaCell label="Ultimo contatto"><span className="text-sm">{lead.last_contact_at ? relDate(lead.last_contact_at) : "Mai contattato"}</span></MetaCell>
          <MetaCell label="Prossimo contatto">
            {canWork && nextActivity && lead.next_contact_at ? (
              <button data-testid="next-contact-edit" onClick={() => (nextActivity.kind === "task" ? setEditTask(nextActivity.item) : setEditAppt(nextActivity.item))}
                title="Modifica l'attività programmata"
                className={cn("text-sm font-medium underline decoration-dotted underline-offset-4 hover:text-primary inline-flex items-center gap-1 text-left", lead.next_contact_at && isOverdue(lead.next_contact_at) && "text-amber-600 dark:text-amber-400")}>
                {`${isOverdue(lead.next_contact_at) ? "⚠️ " : ""}${relDate(lead.next_contact_at)}`}
                <Pencil className="w-3 h-3" />
              </button>
            ) : (
              <span className={cn("text-sm font-medium", lead.next_contact_at && isOverdue(lead.next_contact_at) && "text-amber-600 dark:text-amber-400")}>
                {lead.next_contact_at ? `${isOverdue(lead.next_contact_at) ? "⚠️ " : ""}${relDate(lead.next_contact_at)}` : "—"}
              </span>
            )}
          </MetaCell>
          <MetaCell label="Prossima azione"><span className="text-sm">{lead.next_action || "—"}</span></MetaCell>
          <MetaCell label="Agente Affari in Mediazione">
            <Select value={lead.has_agent_mediation || "Non specificato"} onValueChange={(v) => updateField("has_agent_mediation", v)}>
              <SelectTrigger data-testid="agent-mediation-select" className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{["Sì", "No", "Non specificato"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </MetaCell>
          <MetaCell label="Diploma">
            <Select value={lead.has_diploma || "Non specificato"} onValueChange={(v) => updateField("has_diploma", v)}>
              <SelectTrigger data-testid="diploma-select" className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{["Sì", "No", "Non specificato"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </MetaCell>
          <MetaCell label="Commerciale">
            {isAdmin ? (
              <Select value={lead.assigned_to} onValueChange={reassign}>
                <SelectTrigger data-testid="assign-select" className="h-8"><SelectValue /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <span className="inline-flex items-center gap-2 text-sm">{lead.assigned_user && <UserAvatar user={lead.assigned_user} size={22} />}{lead.assigned_user?.name || "—"}</span>
            )}
          </MetaCell>
          {lead.creation_method === "manual" && lead.created_by_name && (
            <MetaCell label="Inserito da"><span className="text-sm" data-testid="lead-created-by">✍️ {lead.created_by_name}</span></MetaCell>
          )}
          {(() => {
            const orig = String(lead.imported_owner_raw || "").trim();
            const curFirst = (lead.assigned_user?.name || "").trim().split(/\s+/)[0] || "";
            const origFirst = orig.split(/\s+/)[0] || "";
            if (!orig || origFirst.toUpperCase() === curFirst.toUpperCase()) return null;
            return (
              <MetaCell label="Consulente originale">
                <span
                  data-testid="lead-original-owner"
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-sm font-medium text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                  title="Consulente che aveva seguito inizialmente questo contatto"
                >
                  <History className="h-3.5 w-3.5" />
                  {orig}
                </span>
              </MetaCell>
            );
          })()}
        </div>
      </div>

      {/* Quick actions (desktop) */}
      <div className="hidden md:flex flex-wrap gap-2">
        {ACTIONS.filter((a) => a.key !== "call" || (lead.phone && !isInvalidPhone(lead.phone))).map((a) => (
          a.key === "call" ? (
            <a key={a.key} href={`tel:${lead.phone}`}
              data-testid="action-call"
              className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors", a.cls)}>
              <a.icon className="w-4 h-4" /> {a.label}
            </a>
          ) : (
            <button key={a.key} data-testid={`action-${a.key}`} onClick={() => setModal(a.key)}
              className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors border",
                a.cls || "bg-card hover:bg-accent")}>
              <a.icon className="w-4 h-4" /> {a.label}
            </button>
          )
        ))}
        {canWork && (
          <button data-testid="action-attempt" onClick={markAttempt}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md font-medium text-sm transition-colors border border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20">
            <PhoneMissed className="w-4 h-4" /> Segna tentativo
          </button>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Timeline */}
        <div className="lg:col-span-2 rounded-lg border bg-card">
          <div className="px-4 py-3 border-b"><h2 className="font-heading font-bold">Timeline</h2></div>
          <div className="p-4">
            {lead.timeline.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nessuna interazione registrata.</p>
            ) : (
              <ol className="relative border-l ml-2 space-y-5">
                {lead.timeline.map((e) => {
                  const m = EVENT_META[e.type] || { icon: "•", label: e.type };
                  return (
                    <li key={e.id} className="ml-5" data-testid={`timeline-${e.type}`}>
                      <span className="absolute -left-[9px] w-[18px] h-[18px] rounded-full bg-background border grid place-items-center text-[10px]">{m.icon}</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{m.label}</span>
                        {e.imported && (
                          <span data-testid="timeline-historical-badge" className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                            Storico
                          </span>
                        )}
                        {e.outcome && <span className="text-xs px-1.5 py-0.5 rounded bg-secondary">{e.outcome}</span>}
                        {e.status && <StatusBadge value={e.status} />}
                        {e.temperature && <TemperatureBadge value={e.temperature} />}
                        <span className="text-xs text-muted-foreground ml-auto">{fmtDateTime(e.created_at)}</span>
                      </div>
                      {e.note && <p className="text-sm text-muted-foreground mt-1">{e.note}</p>}
                      {e.type === "note_edited" && (
                        <div className="text-xs mt-1 space-y-0.5" data-testid={`note-edit-trace-${e.id}`}>
                          <p className="text-muted-foreground line-through">{e.prev_note}</p>
                          <p className="text-foreground">→ {e.new_note}</p>
                        </div>
                      )}
                      {e.imported && e.author_label && <p className="text-[11px] text-amber-700/80 dark:text-amber-400/80 mt-0.5 italic">{e.author_label}</p>}
                      {(e.fonte || e.campaign) && <p className="text-xs text-muted-foreground mt-1">{[e.fonte, e.sorgente, e.campaign].filter(Boolean).join(" · ")}</p>}
                      {e.due_at && <p className="text-xs text-muted-foreground mt-1">Scadenza: {relDate(e.due_at)}</p>}
                      {e.start_at && <p className="text-xs text-muted-foreground mt-1">{relDate(e.start_at)} {e.tipologia ? `· ${e.tipologia}` : ""} {e.mode ? `· ${e.mode}` : ""}</p>}
                      {e.user && <p className="text-[11px] text-muted-foreground mt-0.5">— {e.user.name}</p>}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        {/* Side: deal + acquisitions + appointments */}
        <div className="space-y-4">
          <DealCard lead={lead} canWork={canWork} onDone={load}
            onClose={(result) => setModal(result === "won" ? "deal_won" : "deal_lost")} />
          <div className="rounded-lg border bg-card">
            <div className="px-4 py-3 border-b"><h3 className="font-heading font-bold text-sm">Acquisizioni ({lead.acquisitions.length})</h3></div>
            <div className="p-3 space-y-2">
              {lead.acquisitions.map((a) => (
                <div key={a.id} className="text-xs border rounded-md p-2.5">
                  <div className="font-medium text-sm">{a.service || a.campaign || "Acquisizione"}</div>
                  <div className="text-muted-foreground mt-0.5">{[a.fonte, a.sorgente].filter(Boolean).join(" · ")}</div>
                  <div className="text-muted-foreground">{relDate(a.acquisition_date)}</div>
                  {a.meta_lead_id && <div className="font-mono text-[10px] text-muted-foreground mt-1">{a.meta_lead_id}</div>}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border bg-card">
            <div className="px-4 py-3 border-b"><h3 className="font-heading font-bold text-sm">Appuntamenti ({lead.appointments.length})</h3></div>
            <div className="p-3 space-y-2">
              {lead.appointments.length === 0 ? <p className="text-xs text-muted-foreground px-1">Nessun appuntamento.</p> :
                lead.appointments.map((a) => (
                  <div key={a.id} className="text-xs border rounded-md p-2.5 flex items-center justify-between gap-2">
                    <div>
                      <div className="font-medium">{relDate(a.start_at)}</div>
                      <div className="text-muted-foreground" data-testid={`appt-tipologia-${a.id}`}>{a.tipologia || "Non specificata"} · {a.mode} · {a.duration}min</div>
                    </div>
                    <div className="flex items-center gap-1">
                      {(a.status === "DA FARE" || a.status === "RIPROGRAMMATO") && (
                        <button data-testid={`appt-edit-${a.id}`} onClick={() => setEditAppt(a)} title="Modifica appuntamento"
                          className="w-7 h-7 grid place-items-center rounded-md border hover:bg-accent"><Pencil className="w-3.5 h-3.5" /></button>
                      )}
                      <ApptStatusControl appt={a} onDone={load} />
                      <button data-testid={`appt-delete-${a.id}`} title="Elimina appuntamento"
                        onClick={async () => { if (window.confirm("Sei sicuro di voler eliminare questa attività?")) { await api.delete(`/appointments/${a.id}`); toast.success("Appuntamento eliminato"); load(); } }}
                        className="w-7 h-7 grid place-items-center rounded-md border border-red-500/30 text-red-600 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky action bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-background/95 backdrop-blur border-t p-2 flex gap-2">
        {lead.phone && !isInvalidPhone(lead.phone) && <a href={`tel:${lead.phone}`} className="flex-1 inline-flex items-center justify-center gap-1 py-2.5 rounded-md bg-emerald-500 text-white font-medium text-sm"><Phone className="w-4 h-4" />Chiama</a>}
        <button onClick={() => setModal("appointment")} data-testid="action-appointment-mobile" className="flex-1 inline-flex items-center justify-center gap-1 py-2.5 rounded-md border text-sm"><CalendarPlus className="w-4 h-4" />Appuntamento</button>
        {canWork && <button data-testid="action-attempt-mobile" onClick={markAttempt} className="flex-1 inline-flex items-center justify-center gap-1 py-2.5 rounded-md border border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm"><PhoneMissed className="w-4 h-4" />Tentativo</button>}
      </div>

      {/* Modals */}
      <NoteModal open={modal === "note"} onClose={() => setModal(null)} leadId={id} onDone={load} />
      <RequiredActionModal status={pendingStatus} onClose={() => setPendingStatus(null)} leadId={id} onDone={load} />
      <AppointmentModal open={modal === "appointment"} onClose={() => setModal(null)} leadId={id} onDone={load} />
      <EditAppointmentModal appt={editAppt} open={!!editAppt} onClose={() => setEditAppt(null)} onDone={load}
        extraActions={editAppt && editAppt.status === "DA FARE" ? <ActivityActions appt={editAppt} onDone={() => { setEditAppt(null); load(); }} /> : null} />
      <EditActivityModal task={editTask} open={!!editTask} onClose={() => setEditTask(null)} onDone={load}
        extraActions={editTask ? <ActivityActions task={editTask} onDone={() => { setEditTask(null); load(); }} /> : null} />
      <WhatsAppLeadDialog open={waOpen} onClose={() => setWaOpen(false)} lead={lead} userName={user?.name} onLogged={load} />
      <EditLeadModal open={showEditLead} onClose={() => setShowEditLead(false)} lead={lead} onDone={load} />
      <FollowupModal open={modal === "followup"} onClose={() => setModal(null)} leadId={id} onDone={load} />
      <TemperatureModal open={modal === "temperature"} onClose={() => setModal(null)} current={lead.temperature} onSelect={quickTemp} />
      <CloseDealModal open={modal === "deal_won" || modal === "deal_lost"} result={modal === "deal_won" ? "won" : "lost"}
        onClose={() => setModal(null)} leadId={id} currentValue={lead.deal_value} onDone={load} />
    </div>
  );
}

function DealCard({ lead, canWork, onDone, onClose }) {
  const [val, setVal] = useState(lead.deal_value ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(lead.deal_value ?? ""); }, [lead.deal_value]);

  const save = async () => {
    setSaving(true);
    await api.put(`/leads/${lead.id}`, { deal_value: val === "" ? null : Number(val) });
    setSaving(false);
    toast.success("Valore trattativa aggiornato");
    onDone();
  };
  const reopen = async () => {
    try { await api.post(`/leads/${lead.id}/reopen-deal`); toast.success("Trattativa riaperta"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const ds = lead.deal_status || "open";
  const statusMeta = {
    open: { label: "Aperta", cls: "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300" },
    won: { label: "Vinta", cls: "bg-emerald-500 text-white" },
    lost: { label: "Persa", cls: "bg-red-500 text-white" },
  }[ds];

  return (
    <div className="rounded-lg border-2 border-emerald-500/25 bg-card" data-testid="deal-card">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-heading font-bold text-sm flex items-center gap-1.5"><Euro className="w-4 h-4 text-emerald-500" />Trattativa</h3>
        <span className={cn("text-xs px-2 py-0.5 rounded-full font-semibold", statusMeta.cls)}>{statusMeta.label}</span>
      </div>
      <div className="p-3 space-y-3">
        <div>
          <label className="overline text-muted-foreground">Valore trattativa (€)</label>
          <div className="flex gap-2 mt-1">
            <Input type="number" min="0" step="100" value={val} data-testid="deal-value-input"
              onChange={(e) => setVal(e.target.value)} placeholder="0" className="h-9" />
            <Button size="sm" onClick={save} disabled={saving} data-testid="deal-value-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
          </div>
          {lead.deal_value ? <div className="text-lg font-heading font-black mt-1.5 tabular-nums">{fmtEuro(lead.deal_value)}</div> : null}
        </div>

        {ds === "open" ? (
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" onClick={() => onClose("won")} data-testid="deal-close-won" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1"><Trophy className="w-3.5 h-3.5" />Vinta</Button>
            <Button size="sm" onClick={() => onClose("lost")} data-testid="deal-close-lost" variant="outline" className="border-red-500/40 text-red-600 hover:bg-red-500/10 gap-1"><XCircle className="w-3.5 h-3.5" />Persa</Button>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground space-y-1">
            <div>Chiusa il {fmtDateTime(lead.closed_at)}</div>
            {lead.lost_reason && <div>Motivo: <b>{lead.lost_reason}</b></div>}
            {canWork && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                <Button size="sm" variant="outline" onClick={reopen} className="gap-1 h-7" data-testid="deal-reopen"><Unlock className="w-3.5 h-3.5" />Riapri trattativa</Button>
                {ds === "lost" ? (
                  <Button size="sm" onClick={() => onClose("won")} className="gap-1 h-7 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="deal-change-won"><Trophy className="w-3.5 h-3.5" />Cambia in Vinta</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => onClose("lost")} className="gap-1 h-7 border-red-500/40 text-red-600 hover:bg-red-500/10" data-testid="deal-change-lost"><XCircle className="w-3.5 h-3.5" />Cambia in Persa</Button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CloseDealModal({ open, result, onClose, leadId, currentValue, onDone }) {
  const [value, setValue] = useState(currentValue ?? "");
  const [note, setNote] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const won = result === "won";

  useEffect(() => { setValue(currentValue ?? ""); }, [currentValue, open]);
  useEffect(() => { if (open && !won) api.get("/lost-reasons").then((r) => setReasons(r.data.filter((x) => x.active))); }, [open, won]);

  const save = async () => {
    if (!won && !lostReason) { toast.error("Seleziona un motivo di perdita"); return; }
    setSaving(true);
    await api.post(`/leads/${leadId}/close-deal`, {
      result, value: value === "" ? null : Number(value), note,
      lost_reason: won ? null : lostReason,
    });
    setSaving(false);
    toast.success(won ? "Trattativa vinta! 🎉" : "Trattativa chiusa come persa");
    setNote(""); setLostReason("");
    onClose(); onDone();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2">
          {won ? <><Trophy className="w-5 h-5 text-emerald-500" />Chiusa / Vinta</> : <><XCircle className="w-5 h-5 text-red-500" />Chiusa / Persa</>}
        </DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs">Valore trattativa (€)</Label>
            <Input type="number" min="0" step="100" value={value} data-testid="close-deal-value" onChange={(e) => setValue(e.target.value)} placeholder="0" />
          </div>
          {!won && (
            <div className="space-y-1">
              <Label className="text-xs">Motivo perdita</Label>
              <Select value={lostReason} onValueChange={setLostReason}>
                <SelectTrigger data-testid="lost-reason-select"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                <SelectContent>{reasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs">Nota (opzionale)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="close-deal-note" />
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="close-deal-save" onClick={save} disabled={saving}
            className={won ? "bg-emerald-500 hover:bg-emerald-600 text-white" : "bg-red-500 hover:bg-red-600 text-white"}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{won ? "Segna come vinta" : "Segna come persa"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MetaCell({ label, children }) {
  return (
    <div>
      <div className="overline text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}

function ApptStatusControl({ appt, onDone }) {
  const set = async (status) => { await api.put(`/appointments/${appt.id}/status`, { status }); toast.success("Aggiornato"); onDone(); };
  const badge = { "DA FARE": "bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300", "EFFETTUATO": "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300", "NO-SHOW": "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300", "ANNULLATO": "bg-zinc-200 text-zinc-600 dark:bg-zinc-800", "RIPROGRAMMATO": "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300" }[appt.status];
  return (
    <Select value={appt.status} onValueChange={set}>
      <SelectTrigger className={cn("h-7 text-[11px] w-auto gap-1 border-0", badge)} data-testid={`appt-status-${appt.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {["DA FARE", "EFFETTUATO", "NO-SHOW", "ANNULLATO"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function NoteModal({ open, onClose, leadId, onDone }) {
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const loadNotes = useCallback(() => {
    if (leadId) api.get(`/leads/${leadId}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [leadId]);
  useEffect(() => { if (open) loadNotes(); }, [open, loadNotes]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    await api.post(`/leads/${leadId}/note`, { text });
    setSaving(false);
    toast.success("Nota aggiunta");
    setText("");
    loadNotes();
    onDone();
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) { toast.error("Nota vuota"); return; }
    try {
      await api.put(`/leads/${leadId}/notes/${id}`, { text: editText });
      toast.success("Nota modificata");
      setEditingId(null); setEditText("");
      loadNotes(); onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><StickyNote className="w-5 h-5" />Note</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs mb-1.5 block">Nuova nota</Label>
            <Textarea data-testid="note-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Scrivi una nuova nota…" rows={3} />
            <div className="flex justify-end mt-2">
              <Button data-testid="note-save" onClick={save} disabled={saving} size="sm">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva nota</Button>
            </div>
          </div>
          <div>
            <Label className="text-xs mb-1.5 block">Tutte le note ({notes.length})</Label>
            <div className="max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="all-notes">
              {notes.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nessuna nota ancora.</p> :
                notes.map((n) => (
                  <div key={n.id} data-testid={`note-item-${n.id}`} className={cn("rounded-md border p-3 text-sm", n.imported && "border-amber-500/30 bg-amber-500/5")}>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="font-medium text-foreground flex items-center gap-1.5">
                        {n.imported && <span className="text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">Storico</span>}
                        {n.imported ? (n.author_label || "Storico") : (n.user?.name || "—")}
                      </span>
                      <span className="flex items-center gap-2">
                        {n.edited_at && <span className="italic">modificata</span>}
                        <span>{fmtDateTime(n.created_at)}</span>
                        {!n.imported && editingId !== n.id && (
                          <button data-testid={`note-edit-${n.id}`} onClick={() => { setEditingId(n.id); setEditText(n.note || ""); }} title="Modifica nota" className="text-muted-foreground hover:text-foreground">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </span>
                    </div>
                    {editingId === n.id ? (
                      <div className="space-y-2">
                        <Textarea data-testid={`note-edit-text-${n.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} />
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditText(""); }}>Annulla</Button>
                          <Button size="sm" data-testid={`note-edit-save-${n.id}`} onClick={() => saveEdit(n.id)}>Salva</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{n.note}</p>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AppointmentModal({ open, onClose, leadId, onDone }) {
  const [dt, setDt] = useState("");
  const [duration, setDuration] = useState("30");
  const [mode, setMode] = useState("Telefonico");
  const [tipologia, setTipologia] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const { data: avail } = useApptAvailability(dt, duration, null);
  const hasConflict = !!avail && (avail.conflicts?.length > 0 || avail.google_conflicts?.length > 0);

  const save = async (force = false) => {
    if (!dt) { toast.error("Seleziona data e ora"); return; }
    if (!tipologia) { toast.error("Seleziona la tipologia"); return; }
    setSaving(true);
    try {
      await api.post("/appointments", { lead_id: leadId, start_at: new Date(dt).toISOString(), duration: parseInt(duration), mode, tipologia, note, force });
      toast.success("Appuntamento fissato");
      setSaving(false); setDt(""); setNote(""); setTipologia(""); onClose(); onDone();
    } catch (e) {
      setSaving(false);
      if (e.response?.status === 409) {
        if (window.confirm("⚠️ Hai già un appuntamento in questa fascia oraria. Procedere comunque?")) save(true);
      } else toast.error("Errore");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Fissa appuntamento</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Tipologia</Label>
            <div className="grid grid-cols-3 gap-1.5">
              {APPT_TYPES.map((t) => (
                <button key={t} type="button" data-testid={`appt-type-${t}`} onClick={() => setTipologia(t)}
                  className={cn("px-2 py-1.5 rounded-md text-xs border transition-colors", tipologia === t ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}>{t}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Data e ora</Label><Input data-testid="appt-datetime" type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Durata (min)</Label>
              <Select value={duration} onValueChange={setDuration}><SelectTrigger data-testid="appt-duration"><SelectValue /></SelectTrigger><SelectContent>{["15", "30", "45", "60", "90"].map((d) => <SelectItem key={d} value={d}>{d} min</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Modalità</Label>
              <Select value={mode} onValueChange={setMode}><SelectTrigger data-testid="appt-mode"><SelectValue /></SelectTrigger><SelectContent>{APPT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota…" rows={2} />
          <ApptConflictBanner data={avail} />
        </div>
        <DialogFooter><Button data-testid="appt-save" onClick={() => save(hasConflict)} disabled={saving} className={hasConflict ? "bg-amber-600 hover:bg-amber-700" : ""}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{hasConflict ? "Fissa comunque" : "Fissa appuntamento"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FollowupModal({ open, onClose, leadId, onDone }) {
  const [when, setWhen] = useState("tomorrow");
  const [customDt, setCustomDt] = useState("");
  const [action, setAction] = useState("Ricontattare");
  const save = async () => {
    const payload = { when, action };
    if (when === "custom") payload.datetime = customDt ? new Date(customDt).toISOString() : undefined;
    await api.post(`/leads/${leadId}/followup`, payload);
    toast.success("Follow-up programmato"); onClose(); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Programma follow-up</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Prossima azione</Label><Input data-testid="followup-action" value={action} onChange={(e) => setAction(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Quando</Label>
            <Select value={when} onValueChange={setWhen}><SelectTrigger data-testid="followup-when2"><SelectValue /></SelectTrigger><SelectContent>{FOLLOWUP_OPTIONS.filter((o) => o.key !== "none").map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent></Select>
            {when === "custom" && <Input type="datetime-local" className="mt-2" value={customDt} onChange={(e) => setCustomDt(e.target.value)} />}
          </div>
        </div>
        <DialogFooter><Button data-testid="followup-save" onClick={save}>Programma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Stati con attività programmata FACOLTATIVA: il promemoria nasce solo se si sceglie data e ora
function RequiredActionModal({ status, onClose, leadId, onDone }) {
  const [when, setWhen] = useState("tomorrow");
  const [customDt, setCustomDt] = useState("");
  const [action, setAction] = useState("Inviare mail");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (status) { setWhen("tomorrow"); setCustomDt(""); setAction("Inviare mail"); } }, [status]);
  const save = async (withActivity) => {
    let followup;
    if (withActivity) {
      if (!action.trim()) { toast.error("Indica l'attività"); return; }
      if (when === "custom" && !customDt) { toast.error("Scegli data e ora"); return; }
      followup = { when, action };
      if (when === "custom") followup.datetime = new Date(customDt).toISOString();
    }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/status`, followup ? { status, followup } : { status });
      toast.success(followup ? "Stato salvato e attività programmata (riceverai un promemoria)" : "Stato aggiornato");
      onClose(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSaving(false);
  };
  return (
    <Dialog open={!!status} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="required-action-modal">
        <DialogHeader><DialogTitle className="font-heading">Stato «{status}»</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">Facoltativo: programma un'attività con data e ora per ricevere un promemoria. Nessuna email viene inviata automaticamente.</p>
          <div className="space-y-1"><Label className="text-xs">Prossima azione</Label><Input data-testid="required-action-text" value={action} onChange={(e) => setAction(e.target.value)} /></div>
          <div className="space-y-1"><Label className="text-xs">Quando</Label>
            <Select value={when} onValueChange={setWhen}><SelectTrigger data-testid="required-action-when"><SelectValue /></SelectTrigger><SelectContent>{FOLLOWUP_OPTIONS.filter((o) => o.key !== "none").map((o) => <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>)}</SelectContent></Select>
            {when === "custom" && <Input type="datetime-local" className="mt-2" data-testid="required-action-datetime" value={customDt} onChange={(e) => setCustomDt(e.target.value)} />}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button variant="outline" data-testid="required-action-save-status-only" onClick={() => save(false)} disabled={saving}>Salva solo stato</Button>
          <Button data-testid="required-action-save" onClick={() => save(true)} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva con attività</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemperatureModal({ open, onClose, current, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xs">
        <DialogHeader><DialogTitle className="font-heading">Cambia temperatura</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {TEMP_ORDER.map((k) => (
            <button key={k} data-testid={`temp-set-${k}`} onClick={() => { onSelect(k); onClose(); }}
              className={cn("w-full flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm font-medium transition-colors", current === k ? TEMPERATURES[k].badge : "hover:bg-accent")}>
              <span>{TEMPERATURES[k].emoji}</span> {TEMPERATURES[k].label}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
